import React, { ChangeEvent, useState } from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { ColorPalette, TypographySettings } from '../../../../../components/config';
import { SingleSelect } from '../../../../../components/single-select';
import { useSavedQueriesByApplicationAreaId } from '../../../../efforts/company-recruitments/hooks/use-saved-queries-by-application-area-id';
import { IQueryDefinition } from '../../../../search/models/dtos';
import { useAuthenticatedUser } from '../../../../../authentication';
import { usePostToTab } from '../../../../search/hooks/usePostToTab';
import { FormDialog } from '../../ruling/components/form-dialog';

interface ICvFurtherSearchProps {
  selectedCitizens: GridRowId[];
  onFurtherSearchClick: () => void;
}

export function CvFurtherSearchDialog(props: ICvFurtherSearchProps) {
  const { selectedCitizens, onFurtherSearchClick } = props;

  const user = useAuthenticatedUser();

  const searchInOwnCitizensApplicationAreaId = 'EAD4638B-5C48-4058-9B83-959885EABC76';
  const savedQueries = useSavedQueriesByApplicationAreaId(searchInOwnCitizensApplicationAreaId);
  function getFilteredQueries() {
    const filteredSavedQueries = savedQueries.filter(query => !showOnlyMyWorklists || query.ownedById === user?.profile.id || !!query.isSharedOrOwner);
    return filteredSavedQueries.sort((a, b) => a.name.localeCompare(b.name));
  }

  const [selectedWorklist, setSelectedWorklist] = useState<IQueryDefinition>();

  const [showOnlyMyWorklists, setShowOnlyMyWorklists] = useState(true);

  function handleWorklistSelected(value: IQueryDefinition) {
    setSelectedWorklist(value);
  }

  function handleShowOnlyMyWorklistsChange(_: ChangeEvent<HTMLInputElement>, checked: boolean): void {
    setShowOnlyMyWorklists(checked);
  }

  const post = usePostToTab('cv-further-search');

  function handleSearchInOwnCitizens(cprs: string[], queryId: string) {
    const applicationArea = 'Søg videre - Smart match';
    // eslint-disable-next-line max-len
    const url = `/search/worklist?queryId=${queryId}&showQuery=true&showQuerySelector=true&showFilter=true&referrer=${
      window.location.origin
    }&applicationArea=${applicationArea}&referrerKey=SmartMatchRefKey&timestamp=${new Date().getTime()}#Non Dashboard`;
    const win = window.open(url, 'rel=origin');

    const civilRegistrationIdentifiers = selectedCitizens as string[];
    post(
      win,
      civilRegistrationIdentifiers.map(x => `${x.slice(0, 6)}-${x.slice(6, 10)}`)
    );
    onFurtherSearchClick();
  }

  function handleSearchInOwnCitizensClick() {
    if (!selectedWorklist) {
      return;
    }
    const civilRegistrationIdentifiers = selectedCitizens as string[];
    handleSearchInOwnCitizens(civilRegistrationIdentifiers, selectedWorklist.id);
  }

  return (
    <FormDialog
      open
      onConfirmClick={handleSearchInOwnCitizensClick}
      title="Fortsæt søgning i arbejdsliste med egne borgere"
      confirmButtonText="Søg videre i egne borgere"
      isValid={!!selectedWorklist}
      onCancelClick={onFurtherSearchClick}
      cancelButtonText="Afbryd"
    >
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" gap={1} alignItems="center" sx={{ fontWeight: TypographySettings.AlternativeFontLight }}>
          <FontAwesomeIcon size="lg" icon={faInfoCircle} color={ColorPalette.InfoIcon} /> For at arbejde videre med de fremsøgte borgere i en arbejdsliste, skal du først vælge en
          arbejdsliste og markere de fremsøgte borgere, du ønsker at arbejde videre med
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <SingleSelect<IQueryDefinition>
            label="Vælg arbejdsliste"
            getOptionLabel={x => x.name || ''}
            onChange={handleWorklistSelected}
            options={getFilteredQueries()}
            value={selectedWorklist}
            variant="outlined"
            renderOption={(prop: object, option: IQueryDefinition) => (
              <li {...prop} key={option.id}>
                {option.name}
              </li>
            )}
          />
        </Box>
        <Box>
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              <FormControlLabel
                control={<Checkbox size="small" name="showOnlyMyWorklists" onChange={handleShowOnlyMyWorklistsChange} checked={showOnlyMyWorklists} />}
                label="Vis kun arbejdslister oprettet af mig"
              />
            </FormGroup>
          </FormControl>
        </Box>
      </Box>
    </FormDialog>
  );
}
